import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { hubApi } from "../services/hubApi";

export default function VoIPSettings({ connectionId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!connectionId) return;
    setLoading(true);
    try {
      const { data } = await hubApi.voipGetConfig(connectionId);
      setConfig(data.config || {});
    } catch (err) {
      console.error("Error loading VoIP config:", err);
      setError("Erro ao carregar configuracoes");
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleToggle = (key) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
    setSuccess(false);
  };

  const handleChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await hubApi.voipUpdateConfig(connectionId, {
        enableIncoming: config.enableIncoming,
        enableRecording: config.enableRecording,
        autoReject: config.autoReject,
        engine: config.engine,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar configuracoes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: "#8B5CF6" }} />
      </Box>
    );
  }

  if (!config) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        Nenhuma configuracao VoIP encontrada para esta conexao.
      </Alert>
    );
  }

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#8B5CF6" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      bgcolor: "rgba(139, 92, 246, 0.5)",
    },
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#F3F4F6", mb: 3 }}>
        Configuracoes VoIP
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          Configuracoes salvas com sucesso!
        </Alert>
      )}

      <Card
        sx={{
          bgcolor: "#1E293B",
          border: "1px solid rgba(255,255,255,0.04)",
          borderRadius: "14px",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Toggles */}
          <Typography
            variant="subtitle2"
            sx={{ color: "#9CA3AF", mb: 2, fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
          >
            Chamadas
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enableIncoming ?? true}
                  onChange={() => handleToggle("enableIncoming")}
                  sx={switchSx}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: "#F3F4F6", fontSize: "0.9rem", fontWeight: 500 }}>
                    Receber chamadas
                  </Typography>
                  <Typography sx={{ color: "#6B7280", fontSize: "0.75rem" }}>
                    Permite que chamadas entrantes sejam recebidas
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config.autoReject ?? false}
                  onChange={() => handleToggle("autoReject")}
                  sx={switchSx}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: "#F3F4F6", fontSize: "0.9rem", fontWeight: 500 }}>
                    Rejeitar automaticamente
                  </Typography>
                  <Typography sx={{ color: "#6B7280", fontSize: "0.75rem" }}>
                    Rejeita chamadas entrantes automaticamente
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config.enableRecording ?? true}
                  onChange={() => handleToggle("enableRecording")}
                  sx={switchSx}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: "#F3F4F6", fontSize: "0.9rem", fontWeight: 500 }}>
                    Gravar chamadas
                  </Typography>
                  <Typography sx={{ color: "#6B7280", fontSize: "0.75rem" }}>
                    Salva gravacao de audio das chamadas
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", my: 2.5 }} />

          {/* Engine */}
          <Typography
            variant="subtitle2"
            sx={{ color: "#9CA3AF", mb: 2, fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
          >
            Motor de chamadas
          </Typography>

          <FormControl
            size="small"
            sx={{
              width: 250,
              mb: 3,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#111827",
                borderRadius: "10px",
                "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
              },
              "& .MuiInputLabel-root": { color: "#6B7280" },
              "& .MuiSelect-select": { color: "#F3F4F6" },
            }}
          >
            <InputLabel>Engine</InputLabel>
            <Select
              value={config.engine || "auto"}
              label="Engine"
              onChange={(e) => handleChange("engine", e.target.value)}
            >
              <MenuItem value="auto">Auto (Whatomate + SIP fallback)</MenuItem>
              <MenuItem value="whatomate">Whatomate (WhatsApp nativo)</MenuItem>
              <MenuItem value="wuzapi_sip">WuzAPI + SIP Trunk</MenuItem>
            </Select>
          </FormControl>

          {/* Save */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: "#8B5CF6",
                "&:hover": { bgcolor: "#7C3AED" },
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {saving ? "Salvando..." : "Salvar Configuracoes"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
